import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { loginStart, loginSuccess, loginFailure, clearError } from '../store/slices/authSlice';
import { setLoading } from '../store/slices/uiSlice';
import { authService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { Button, Input, Card, CardContent } from '../components';
import { LoginRequest } from '../types/api';

export function Login() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, error } = useAppSelector(state => state.auth);
  const { loading } = useAppSelector(state => state.ui) as any;
  const { login: authHookLogin } = useAuth();

  const [formData, setFormData] = useState<LoginRequest>({
    email: '',
    password: '',
  });
  const [formErrors, setFormErrors] = useState<Partial<LoginRequest>>({});

  // Rediriger si déjà connecté
  useEffect(() => {
    if (authService.isAuthenticated()) {
      navigate('/dashboard');
    }
  }, [navigate]);

  // Nettoyer les erreurs au montage
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const validateForm = (): boolean => {
    const errors: Partial<LoginRequest> = {};

    if (!formData.email) {
      errors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Format d\'email invalide';
    }

    if (!formData.password) {
      errors.password = 'Le mot de passe est requis';
    } else if (formData.password.length < 6) {
      errors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      dispatch(loginStart());
      dispatch(setLoading({ type: 'auth', loading: true }));

      // Utiliser le hook useAuth pour bénéficier du rafraîchissement automatique du cache
      await authHookLogin(formData);

      // Mettre à jour le state Redux avec les données de l'utilisateur
      const currentUser = await authService.getCurrentUser();
      dispatch(loginSuccess({
        user: currentUser,
        token: authService.getToken() || '',
      }));

      console.log('🔄 [LOGIN] Connexion réussie, rechargement complet de la page...');
      
      // Forcer un rechargement complet pour garantir le rafraîchissement de tous les composants
      window.location.href = '/dashboard';
    } catch (error: any) {
      dispatch(loginFailure(error.message || 'Erreur lors de la connexion'));
    } finally {
      dispatch(setLoading({ type: 'auth', loading: false }));
    }
  };

  const handleInputChange = (field: keyof LoginRequest) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));

    // Nettoyer l'erreur du champ quand l'utilisateur tape
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full mx-6">
        <div className="text-center mb-12">
          <div className="flex justify-center mb-8">
            <img
              src="/images/logo-digimanager-rect-rmbg.png"
              alt="DigiManager Logo"
              className="w-auto object-contain"
            />
          </div>
        </div>

        <Card className="bg-white border border-gray-200 shadow-lg">
          <CardContent className="p-8">
            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="text-base text-red-600 flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span>{error}</span>
                  </div>
                </div>
              )}

              <Input
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                error={formErrors.email}
                placeholder="votre.email@exemple.com"
                disabled={isLoading}
                required
                variant="default"
                icon={
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                }
              />

              <Input
                label="Mot de passe"
                type="password"
                value={formData.password}
                onChange={handleInputChange('password')}
                error={formErrors.password}
                placeholder="••••••••"
                disabled={isLoading}
                required
                variant="default"
                icon={
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                }
              />

              <Button
                type="submit"
                variant="gradient"
                size="xl"
                className="w-full"
                isLoading={isLoading}
                disabled={isLoading}
                fullWidth
              >
                {isLoading ? 'Connexion en cours...' : 'Se connecter'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="text-center mt-8">
          <p className="text-base text-gray-500 bg-gray-100 rounded-full px-4 py-2 inline-block">
            Version 1.0.0 - DigiManager
          </p>
        </div>
      </div>
    </div>
  );
}